import React, { useEffect, useState } from "react";
import AnimateCC from "../animate";
import { useUserContext } from "../context/UserContext";
import { additionalSounds } from "./mainscreenSounds";
import { MainscreenMenu } from "./MainscreenMenu";
import { useLocation } from "react-router-dom";
import { routes } from "../navigation/routesPath";
import { displayIf } from "../utils/StyleUtils";
import { InitialScreenComponent } from "./initialScreen/InitialScreenComponent";
import { MC } from "../animate/utils";
import { useMovieContext } from "../context/MovieContext";
import { useIframeContext } from "../context/IframeContext";
import LoginComponent from "../loginnew/LoginComponent";
import { SodMayaTracking } from "../navigation/GoogleAnalytics";
import { Navigation } from "../navigation/navigation";
import { PreloaderComponent } from "../loaders/PreloaderComponent";
import SessionManagerComponent from "../sessionManager/sessionManagerComponent";

// Marks that the user already passed the initial screen in this tab, so we can skip it
// when the page reloads mid-session (e.g. the full-page redirect back from the IdP login).
// sessionStorage survives same-tab navigations but not new tabs, like the popup's window.name did.
const ENTERED_SITE_KEY = "sodmayaEnteredSite";

function SafeURL(url: string) {
    let resultUrl;
    try {
        resultUrl = new URL(url).hostname.endsWith(".cet.ac.il") ? url : "";
    } catch (e) {
        resultUrl = "";
    }

    return resultUrl;
}

const HomeComponent: React.FC = () => {
    const { pathname } = useLocation();
    const directLink = new URLSearchParams(window.location.search).get("path");
    const currentDateParam = new URLSearchParams(window.location.search).get("currentDate");
    const isFromExternalLogin = new URLSearchParams(window.location.search).has("autologin");
    const externalIframe = SafeURL(new URLSearchParams(window.location.search).get("iframe"));

    const currentDate = currentDateParam ? new Date(currentDateParam) : new Date();

    const [showInitialScreen, setShowInitialScreen] = useState(
        !isFromExternalLogin && sessionStorage.getItem(ENTERED_SITE_KEY) === null
    );
    const [screenReady, setScreenReady] = useState(false);

    useEffect(() => {
        if (!showInitialScreen) {
            sessionStorage.setItem(ENTERED_SITE_KEY, "1");
        }
    }, [showInitialScreen]);

    const { isLoggedIn, permissions, logout, currentUser } = useUserContext();

    const movieContext = useMovieContext();

    const mainScreen = React.useRef(new MainscreenMenu(isLoggedIn, logout, permissions, movieContext));

    const { iframeSrc, showLogin, setShowLogin } = useIframeContext();

    useEffect(() => {
        console.log("!!!", mainScreen.current.isLoaded);

        mainScreen.current.updatePermissions(permissions);
    }, [permissions]);

    useEffect(() => {
        const w = window as any;
        w.startGame = (path?: string) => {
            setShowInitialScreen(false);
            if (path) {
                Navigation.openPage(path);
            }
        };
        return () => {
            delete w.startGame;
        };
    }, []);

    useEffect(() => {
        if (screenReady) {
            mainScreen.current.initEnterBtn(isLoggedIn);
        }
    }, [isLoggedIn, screenReady, currentUser, pathname]);

    useEffect(() => {
        if (pathname === routes.home) {
            if (iframeSrc || externalIframe) {
                mainScreen.current.stopSounds();
            } else if (screenReady) {
                mainScreen.current.undoMagic();
            } else {
                mainScreen.current.updateMuteStatus();
            }
        }
    }, [externalIframe, iframeSrc, pathname, screenReady]);

    useEffect(() => {
        if (screenReady && (directLink || externalIframe)) {
            const { location } = window;
            var clean_uri = location.protocol + "//" + location.host + location.pathname;
            window.history.replaceState({}, document.title, clean_uri);
        }
    }, [screenReady, directLink, externalIframe]);

    useEffect(() => {
        if (pathname === routes.home) {
            if (showInitialScreen) {
                SodMayaTracking("/");
            } else {
                SodMayaTracking("/menu");
            }
        }
    }, [pathname, showInitialScreen]);

    useEffect(() => {
        if (externalIframe) {
            setShowInitialScreen(false);
            Navigation.openIframe(externalIframe);
        }
    }, [externalIframe]);

    return showInitialScreen ? (
        <InitialScreenComponent
            style={displayIf(pathname === routes.home)}
            showMayaRoom={() => {
                setShowInitialScreen(false);
                if (directLink) {
                    Navigation.openPage(directLink);
                }
            }}
        />
    ) : (
        <>
            {!iframeSrc && !screenReady && (!directLink || (directLink && pathname === routes.home)) && (
                <PreloaderComponent name="plain" />
            )}
            {(!directLink || (directLink && pathname === routes.home)) && (
                <AnimateCC
                    base="/mainscreen"
                    animationName="mainscreen"
                    onLoaded={(mc: MC) => {
                        mainScreen.current.loaded(mc, currentDate, directLink === null && externalIframe === null);
                        setScreenReady(true);
                    }}
                    additionalSounds={additionalSounds}
                    visible={!iframeSrc && pathname === routes.home}
                />
            )}
            {iframeSrc && (
                <iframe
                    id="parentInfoIframe"
                    title="info"
                    src={iframeSrc}
                    style={{
                        border: 0,
                        margin: "0",
                        display: "block",
                        height: "100%",
                        width: "100%",
                        overflow: "hidden",
                        ...displayIf(!showLogin),
                    }}
                />
            )}
            {iframeSrc && showLogin && (
                <LoginComponent
                    loginAfterLocation={"/"}
                    onLoggedIn={() => {
                        (document.getElementById(
                            "parentInfoIframe"
                        ) as HTMLIFrameElement).contentWindow.location.reload();
                        setShowLogin(false);
                    }}
                />
            )}
            {isFromExternalLogin && !isLoggedIn && <SessionManagerComponent />}
        </>
    );
};

export default HomeComponent;
